import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import {
  AUFGABEN_GROESSEN,
  planEntwurfOffen,
  planPruefen,
  TASK_PRIORITIES,
  ZEITFENSTER,
  ZEITFENSTER_KURZ,
  type PlanBefund,
  type PlanPosten,
} from '@netcon-work/shared'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Topbar } from '../components/Topbar'
import { Button, Card, CardPad } from '../components/ui/primitives'
import { cn } from '../lib/cn'
import { Markdown } from '../lib/markdown'
import { useAufgabeOeffnen } from '../components/AufgabeKontext'
import { useTelefonLayout } from '../lib/medien'
import { personenQuery, poolsDerWeltQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'

/**
 * **Entwurfsseite – ein Plan von Claude** ([Z-25](../../../../docs/phasen/zusatz-25-claude-planung.md)
 * Stufe 3, [E-458](../../../../docs/ENTSCHEIDUNGEN.md#e-458)).
 *
 * Claude legt über MCP nur einen Entwurf an. Hier sieht ihn der Mensch als Baum (Vorhaben →
 * Meilensteine → Aufgaben → Unteraufgaben), hakt ab, was nicht übernommen werden soll, ändert
 * jedes Feld direkt, liest die Warnungen und gibt Claude Rückmeldung. Erst „Übernehmen" legt an.
 *
 * Gespeichert wird ohne Knopf: jede Änderung geht nach einer kurzen Pause als Fassung „mensch"
 * an den Server. Hat Claude inzwischen eine neue Fassung geschrieben, wird nicht überschrieben –
 * die Seite sagt es und lädt neu.
 */

export const planEntwurfQuery = (id: string) => ({
  queryKey: ['planEntwurf', id],
  queryFn: () => trpc.planEntwurf.lesen.query({ id }),
  refetchInterval: 20_000,
})

type Sicht = Awaited<ReturnType<typeof trpc.planEntwurf.lesen.query>>

const VIOLETT = 'text-[#5b3fa8]'
const RAHMEN = 'border-[#d9cff5] bg-[#faf8ff]'

const ZEICHEN: Record<PlanPosten['art'], string> = {
  vorhaben: '◆',
  meilenstein: '⚑',
  aufgabe: '☐',
  unteraufgabe: '–',
}

const STAND: Record<Sicht['stand'], string> = {
  offen: 'offen',
  teilweise: 'teilweise übernommen',
  uebernommen: 'übernommen',
  verworfen: 'verworfen',
}

function heute(): string {
  const d = new Date()
  const zwei = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`
}

function datum(t: string | null | undefined): string {
  if (!t) return ''
  const [j, m, d] = t.split('-')
  return `${Number(d)}.${Number(m)}.${j?.slice(2)}`
}

export function PlanEntwurf() {
  const { id = '' } = useParams({ strict: false }) as { id?: string }
  const queryClient = useQueryClient()
  const { data, error } = useQuery({ ...planEntwurfQuery(id), enabled: Boolean(id) })

  if (error) {
    return (
      <>
        <Topbar titel="Entwurf von Claude" />
        <Card>
          <CardPad className="text-[13px] text-ink2">{error.message}</CardPad>
        </Card>
      </>
    )
  }
  if (!data) {
    return <Topbar titel="Entwurf von Claude" untertitel="lädt …" />
  }
  return (
    <EntwurfAnsicht
      key={data.id}
      sicht={data}
      neuLaden={() => void queryClient.invalidateQueries({ queryKey: ['planEntwurf', id] })}
    />
  )
}

function EntwurfAnsicht({ sicht, neuLaden }: { sicht: Sicht; neuLaden: () => void }) {
  const queryClient = useQueryClient()
  const offen = planEntwurfOffen(sicht.stand)
  const [posten, setPosten] = useState<PlanPosten[]>(sicht.posten)
  const [basis, setBasis] = useState(sicht.fassung)
  const [schmutzig, setSchmutzig] = useState(false)
  const [serverBefunde, setServerBefunde] = useState<PlanBefund[]>(sicht.befunde)
  const [meldung, setMeldung] = useState<{ art: 'ok' | 'fehler'; text: string } | null>(null)
  const zeitgeber = useRef<ReturnType<typeof setTimeout> | null>(null)
  /**
   * Die Fassung, auf der gespeichert wird, als Ref und die Speicherungen hintereinander: wer
   * tippt, während die erste noch unterwegs ist, setzt sonst auf der alten Fassung auf – und
   * der Server meldet einen Konflikt mit sich selbst.
   */
  const basisRef = useRef(sicht.fassung)
  const kette = useRef<Promise<unknown>>(Promise.resolve())

  /** Neue Fassung vom Server (Claude hat überarbeitet) – übernehmen, solange nichts ungespeichert ist. */
  useEffect(() => {
    if (!schmutzig && sicht.fassung !== basis) {
      setPosten(sicht.posten)
      setBasis(sicht.fassung)
      basisRef.current = sicht.fassung
      setServerBefunde(sicht.befunde)
    }
  }, [sicht, schmutzig, basis])

  const { data: personen = [] } = useQuery(personenQuery)
  const { data: pools = [] } = useQuery(poolsDerWeltQuery(sicht.welt))
  const { data: meilensteineBestand = [] } = useQuery({
    queryKey: ['vorhaben', 'meilensteine', sicht.zielVorhaben?.id ?? '-'],
    queryFn: () => trpc.vorhaben.meilensteine.query({ vorhabenId: sicht.zielVorhaben!.id }),
    enabled: Boolean(sicht.zielVorhaben),
  })
  const menschen = useMemo(
    () =>
      personen
        .filter((p) => p.isActive && (p.workspaces ?? '').split(',').includes(sicht.welt))
        .map((p) => ({ id: p.id, name: p.displayName })),
    [personen, sicht.welt],
  )
  const nameVon = (pid: string) => menschen.find((m) => m.id === pid)?.name ?? pid

  const speichern = useMutation({
    mutationFn: (v: { posten: PlanPosten[]; basisFassung: number }) =>
      trpc.planEntwurf.aendern.mutate({ id: sicht.id, ...v }),
    onSuccess: (r) => {
      basisRef.current = r.fassung
      setBasis(r.fassung)
      setServerBefunde(r.befunde)
      setSchmutzig(false)
      setMeldung(null)
      void queryClient.invalidateQueries({ queryKey: ['planEntwurf', sicht.id] })
    },
    onError: (e: Error) => {
      setMeldung({ art: 'fehler', text: e.message })
      if (/inzwischen auf Fassung/.test(e.message)) {
        setSchmutzig(false)
        neuLaden()
      }
    },
  })

  const aendern = (neu: PlanPosten[]) => {
    setPosten(neu)
    setSchmutzig(true)
    if (zeitgeber.current) clearTimeout(zeitgeber.current)
    zeitgeber.current = setTimeout(() => {
      kette.current = kette.current
        .then(() => speichern.mutateAsync({ posten: neu, basisFassung: basisRef.current }))
        .catch(() => undefined)
    }, 700)
  }
  useEffect(
    () => () => {
      if (zeitgeber.current) clearTimeout(zeitgeber.current)
    },
    [],
  )

  const postenAendern = (schluessel: string, teil: Partial<PlanPosten>) =>
    aendern(posten.map((p) => (p.schluessel === schluessel ? { ...p, ...teil } : p)))
  const postenWeg = (schluessel: string) =>
    aendern(posten.filter((p) => p.schluessel !== schluessel && p.eltern !== schluessel))
  const postenNeu = (art: 'aufgabe' | 'meilenstein' | 'unteraufgabe', eltern?: string) =>
    aendern([
      ...posten,
      {
        schluessel: `n${Date.now().toString(36)}`,
        art,
        titel:
          art === 'meilenstein'
            ? 'Neuer Meilenstein'
            : art === 'aufgabe'
              ? 'Neue Aufgabe'
              : 'Neuer Schritt',
        an: true,
        ...(eltern ? { eltern } : {}),
      },
    ])

  /** Sofort am Rechner geprüft – Pools und Menschen prüft der Server beim Speichern. */
  const lokal = useMemo(
    () =>
      planPruefen(posten, {
        heute: heute(),
        zielVorhaben: sicht.zielVorhaben ? { zieltermin: sicht.zielVorhaben.zieltermin } : null,
      }),
    [posten, sicht.zielVorhaben],
  )
  const befunde = [...lokal.befunde, ...serverBefunde.filter((b) => b.nurServer)]
  const befundeVon = (k: string) => befunde.filter((b) => b.schluessel === k)
  const allgemein = befunde.filter((b) => !b.schluessel)

  const msEntwurf = posten.filter((p) => p.art === 'meilenstein')
  const vorhaben = posten.find((p) => p.art === 'vorhaben')
  const aufgabenVon = (ms: string | null) =>
    posten.filter(
      (a) =>
        a.art === 'aufgabe' &&
        (ms ? a.eltern === ms : !a.eltern || !msEntwurf.some((m) => m.schluessel === a.eltern)),
    )
  const unterVon = (k: string) => posten.filter((u) => u.art === 'unteraufgabe' && u.eltern === k)

  const zeileProps = {
    gesperrt: !offen,
    pools: pools.map((p) => ({ id: p.id, name: p.label })),
    menschen,
    nameVon,
    meilensteineBestand: meilensteineBestand.map((m) => ({ id: m.id, name: m.label })),
    aendern: postenAendern,
    weg: postenWeg,
  }

  const aufgabeMitUnter = (a: PlanPosten) => (
    <div key={a.schluessel}>
      <PostenZeile p={a} befunde={befundeVon(a.schluessel)} {...zeileProps} />
      <div className="ml-7 border-l border-line pl-2">
        {unterVon(a.schluessel).map((u) => (
          <PostenZeile
            key={u.schluessel}
            p={u}
            befunde={befundeVon(u.schluessel)}
            {...zeileProps}
          />
        ))}
        {offen && !a.ergebnis && (
          <NeuKnopf onClick={() => postenNeu('unteraufgabe', a.schluessel)}>+ Schritt</NeuKnopf>
        )}
      </div>
    </div>
  )

  const fehlerZahl = befunde.filter((b) => b.art === 'fehler').length
  const u = lokal.umfang
  const stunden = Math.round(u.minuten / 60)

  return (
    <>
      <Topbar
        titel="Entwurf von Claude"
        untertitel={
          <a href="/entwuerfe" className="hover:text-accent-deep">
            ← alle Entwürfe
          </a>
        }
      />

      <div className={cn('rounded-[14px] border-2 px-4 py-3', RAHMEN)}>
        <div className={cn('text-[11px] font-bold tracking-[0.08em] uppercase', VIOLETT)}>
          ✦ Entwurf von Claude ·{' '}
          {sicht.stand === 'uebernommen'
            ? 'übernommen'
            : sicht.stand === 'verworfen'
              ? 'verworfen'
              : sicht.stand === 'teilweise'
                ? 'teilweise angelegt'
                : 'noch nichts angelegt'}
        </div>
        <h2 className="mt-1 text-[18px] leading-snug font-bold">{sicht.titel}</h2>
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[12.5px] text-ink2">
          <span>Welt: {sicht.weltName}</span>
          <span>
            {sicht.zielVorhaben
              ? `ergänzt „${sicht.zielVorhaben.name ?? 'Vorhaben'}"`
              : 'legt ein neues Vorhaben an'}
          </span>
          <span>
            Fassung {basis}
            {sicht.fassungen[0]?.von === 'mensch' ? ' (mit deinen Änderungen)' : ''}
          </span>
          <span>{STAND[sicht.stand]}</span>
          <span className="text-muted">
            {schmutzig || speichern.isPending ? 'speichert …' : 'gespeichert'}
          </span>
        </div>
        {sicht.begruendung && (
          <details className="mt-2 text-[13px]" open={sicht.begruendung.length < 600}>
            <summary className={cn('cursor-pointer text-[12.5px] font-semibold', VIOLETT)}>
              Claudes Begründung
            </summary>
            <div className="mt-1">
              <Markdown text={sicht.begruendung} kompakt />
            </div>
          </details>
        )}
      </div>

      {meldung && (
        <p
          className={cn(
            'mt-3 rounded-lg px-3 py-2 text-[12.5px] whitespace-pre-wrap',
            meldung.art === 'fehler' ? 'bg-[#fbe9e7] text-bad' : 'bg-[#e8f5ee] text-good-text',
          )}
        >
          {meldung.text}
        </p>
      )}

      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-[12.5px] text-ink2">
        <strong className="text-ink">Umfang</strong>
        <span>
          {u.meilensteine} Meilenstein{u.meilensteine === 1 ? '' : 'e'} · {u.aufgaben} Aufgabe
          {u.aufgaben === 1 ? '' : 'n'} · {u.unteraufgaben} Schritte
        </span>
        <span>
          {u.groessen.klein} klein · {u.groessen.mittel} mittel · {u.groessen.gross} groß
          {u.groessen.ohne ? ` · ${u.groessen.ohne} ohne Größe` : ''}
        </span>
        <span>≈ {stunden} h</span>
      </div>

      {allgemein.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1">
          {allgemein.map((b) => (
            <BefundZeile key={b.text} b={b} />
          ))}
        </ul>
      )}

      <Card className="mt-3">
        <CardPad className="flex flex-col gap-1">
          {vorhaben && (
            <PostenZeile p={vorhaben} befunde={befundeVon(vorhaben.schluessel)} {...zeileProps} />
          )}
          {sicht.zielVorhaben && (
            <div className="px-1 py-1 text-[13px] font-semibold">
              ◆ {sicht.zielVorhaben.name}{' '}
              <span className="font-normal text-muted">
                (besteht
                {sicht.zielVorhaben.zieltermin
                  ? ` · Ziel ${datum(sicht.zielVorhaben.zieltermin)}`
                  : ''}
                )
              </span>
            </div>
          )}
          <div className="ml-3 flex flex-col gap-1 border-l border-line pl-2">
            {msEntwurf.map((m) => (
              <div key={m.schluessel} className="mt-1">
                <PostenZeile p={m} befunde={befundeVon(m.schluessel)} {...zeileProps} />
                <div className="ml-5 flex flex-col gap-0.5 border-l border-line pl-2">
                  {aufgabenVon(m.schluessel).map(aufgabeMitUnter)}
                  {offen && !m.ergebnis && (
                    <NeuKnopf onClick={() => postenNeu('aufgabe', m.schluessel)}>
                      + Aufgabe
                    </NeuKnopf>
                  )}
                </div>
              </div>
            ))}
            {(aufgabenVon(null).length > 0 || msEntwurf.length === 0) && (
              <div className="mt-1">
                {msEntwurf.length > 0 && (
                  <div className="px-1 py-1 text-[12px] font-semibold text-muted">
                    ohne Meilenstein
                  </div>
                )}
                <div className="flex flex-col gap-0.5">
                  {aufgabenVon(null).map(aufgabeMitUnter)}
                </div>
              </div>
            )}
            {offen && (
              <div className="flex gap-3">
                <NeuKnopf onClick={() => postenNeu('aufgabe')}>+ Aufgabe</NeuKnopf>
                <NeuKnopf onClick={() => postenNeu('meilenstein')}>+ Meilenstein</NeuKnopf>
              </div>
            )}
          </div>
        </CardPad>
      </Card>

      {offen && (
        <Aktionen
          sicht={sicht}
          posten={posten}
          fehlerZahl={fehlerZahl}
          schmutzig={schmutzig || speichern.isPending}
          setMeldung={setMeldung}
          neuLaden={neuLaden}
        />
      )}

      <Rueckmeldung sicht={sicht} gesperrt={!offen} neuLaden={neuLaden} />

      <details className="mt-4 mb-8 text-[12.5px]">
        <summary className="cursor-pointer font-semibold text-ink2">
          Fassungen · {sicht.fassungen.length}
        </summary>
        <ul className="mt-1">
          {sicht.fassungen.map((f) => (
            <li key={f.nummer} className="flex gap-2 border-b border-line py-1 last:border-b-0">
              <span className="tabular w-6 text-muted">{f.nummer}</span>
              <span className={cn('w-14 font-semibold', f.von === 'claude' ? VIOLETT : 'text-ink')}>
                {f.von === 'claude' ? '✦ Claude' : 'du'}
              </span>
              <span className="tabular w-[92px] text-muted">
                {new Date(f.am).toLocaleString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              <span className="min-w-0 flex-1 text-ink2">{f.notiz ?? ''}</span>
            </li>
          ))}
        </ul>
      </details>
    </>
  )
}

function NeuKnopf({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="tap cursor-pointer self-start px-1 py-0.5 text-[12px] font-semibold text-accent-deep"
    >
      {children}
    </button>
  )
}

function BefundZeile({ b }: { b: PlanBefund }) {
  return (
    <li
      className={cn(
        'rounded-md px-2 py-1 text-[12px]',
        b.art === 'fehler' ? 'bg-[#fbe9e7] text-bad' : 'bg-[#fdf3dc] text-[#8a6100]',
      )}
    >
      {b.art === 'fehler' ? '✕ ' : '⚠ '}
      {b.text}
    </li>
  )
}

const FELD = 'rounded-md border border-line2 bg-white px-1.5 py-0.5 text-[12px] disabled:opacity-60'

function PostenZeile({
  p,
  befunde,
  gesperrt,
  pools,
  menschen,
  nameVon,
  meilensteineBestand,
  aendern,
  weg,
}: {
  p: PlanPosten
  befunde: PlanBefund[]
  gesperrt: boolean
  pools: { id: string; name: string }[]
  menschen: { id: string; name: string }[]
  nameVon: (id: string) => string
  meilensteineBestand: { id: string; name: string }[]
  aendern: (schluessel: string, teil: Partial<PlanPosten>) => void
  weg: (schluessel: string) => void
}) {
  const [beschreibungOffen, setBeschreibungOffen] = useState(false)
  /**
   * **Am Telefon eine Zeile je Posten, Felder per Tipp** (AP 25.8). Neun Auswahlfelder je
   * Aufgabe füllen bei 390 px eine ganze Bildschirmhöhe; zugeklappt steht nur, was gesetzt ist.
   */
  const telefon = useTelefonLayout()
  const [aufgeklappt, setAufgeklappt] = useState(false)
  const zu = telefon && !aufgeklappt
  const kurz = [
    p.termin
      ? `${p.art === 'aufgabe' ? 'Frist' : p.art === 'vorhaben' ? 'Ziel' : ''} ${datum(p.termin)}`.trim()
      : null,
    p.zeitfenster ? ZEITFENSTER_KURZ[p.zeitfenster] : null,
    p.groesse ? (p.groesse === 'gross' ? 'groß' : p.groesse) : null,
    p.prioritaet ? `Prio ${p.prioritaet}` : null,
    p.pool ? (pools.find((x) => x.id === p.pool)?.name ?? p.pool) : null,
    ...(p.wunsch ?? []).map((w) => `☆ ${nameVon(w)}`),
    p.bearbeiter ? `👤 ${nameVon(p.bearbeiter)}` : null,
    p.beschreibung ? '¶' : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const fest = gesperrt || Boolean(p.ergebnis)
  const setze = (teil: Partial<PlanPosten>) => aendern(p.schluessel, teil)
  const ohne = (feld: keyof PlanPosten) => aendern(p.schluessel, { [feld]: undefined })
  const istAufgabe = p.art === 'aufgabe'
  const terminName =
    p.art === 'vorhaben'
      ? 'Ziel'
      : p.art === 'meilenstein'
        ? 'Termin'
        : p.art === 'aufgabe'
          ? 'Frist'
          : null

  return (
    <div
      data-posten={p.schluessel}
      className={cn(
        'rounded-lg px-1 py-1',
        !p.an && 'opacity-50',
        p.art === 'vorhaben' && 'bg-[#f6f3fd]',
        p.art === 'meilenstein' && 'bg-[#faf9f6]',
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <input
          type="checkbox"
          aria-label={`${p.titel} übernehmen`}
          checked={p.an || Boolean(p.ergebnis)}
          disabled={fest}
          onChange={(e) => setze({ an: e.target.checked })}
          className="tap h-4 w-4 cursor-pointer"
        />
        <span aria-hidden="true" className={cn('w-3 text-center', VIOLETT)}>
          {ZEICHEN[p.art]}
        </span>
        {zu ? (
          <button
            type="button"
            onClick={() => setAufgeklappt(true)}
            className={cn(
              'min-w-0 flex-1 cursor-pointer px-1 py-0.5 text-left leading-snug',
              p.art === 'vorhaben' || p.art === 'meilenstein'
                ? 'text-[14px] font-semibold'
                : 'text-[13.5px]',
            )}
          >
            {p.titel}
          </button>
        ) : (
          <input
            aria-label="Titel"
            value={p.titel}
            disabled={fest}
            onChange={(e) => setze({ titel: e.target.value })}
            className={cn(
              'min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 hover:border-line2 focus:border-line2 focus:bg-white sm:min-w-[180px]',
              p.art === 'vorhaben' || p.art === 'meilenstein'
                ? 'text-[14px] font-semibold'
                : 'text-[13.5px]',
            )}
          />
        )}
        {p.ergebnis && <ErgebnisVerweis p={p} />}
      </div>

      {zu ? (
        <button
          type="button"
          onClick={() => setAufgeklappt(true)}
          className="mt-0.5 ml-6 cursor-pointer text-left text-[12px] text-ink2"
        >
          {kurz || <span className="text-accent-deep">Felder ▾</span>}
        </button>
      ) : (
        <div className="mt-0.5 ml-6 flex flex-wrap items-center gap-1.5 text-[12px] text-ink2 sm:ml-[38px]">
          {terminName && (
            <label className="flex items-center gap-1">
              {terminName}
              <input
                type="date"
                value={p.termin ?? ''}
                disabled={fest}
                onChange={(e) =>
                  e.target.value ? setze({ termin: e.target.value }) : ohne('termin')
                }
                className={FELD}
              />
            </label>
          )}
          {(istAufgabe || p.art === 'vorhaben') && (
            <Wahl
              name="Zeitfenster"
              wert={p.zeitfenster}
              gesperrt={fest}
              optionen={ZEITFENSTER.map((z) => ({ wert: z, name: ZEITFENSTER_KURZ[z] }))}
              setze={(w) =>
                w ? setze({ zeitfenster: w as PlanPosten['zeitfenster'] }) : ohne('zeitfenster')
              }
            />
          )}
          {istAufgabe && (
            <>
              <Wahl
                name="Größe"
                wert={p.groesse}
                gesperrt={fest}
                optionen={AUFGABEN_GROESSEN.map((g) => ({
                  wert: g,
                  name: g === 'gross' ? 'groß' : g,
                }))}
                setze={(w) =>
                  w ? setze({ groesse: w as PlanPosten['groesse'] }) : ohne('groesse')
                }
              />
              <Wahl
                name="Prio"
                wert={p.prioritaet}
                gesperrt={fest}
                optionen={TASK_PRIORITIES.map((x) => ({ wert: x, name: x }))}
                setze={(w) =>
                  w ? setze({ prioritaet: w as PlanPosten['prioritaet'] }) : ohne('prioritaet')
                }
              />
            </>
          )}
          {(istAufgabe || p.art === 'vorhaben') && (
            <Wahl
              name="Pool"
              wert={p.pool}
              gesperrt={fest}
              optionen={pools.map((x) => ({ wert: x.id, name: x.name }))}
              setze={(w) => (w ? setze({ pool: w }) : ohne('pool'))}
            />
          )}
          {p.art === 'vorhaben' && (
            <Wahl
              name="Typ"
              wert={p.typ}
              gesperrt={fest}
              optionen={[
                { wert: 'thema', name: 'Thema' },
                { wert: 'projekt', name: 'Projekt' },
              ]}
              setze={(w) => (w ? setze({ typ: w as 'thema' | 'projekt' }) : ohne('typ'))}
            />
          )}
          {istAufgabe && meilensteineBestand.length > 0 && !p.eltern && (
            <Wahl
              name="Meilenstein"
              wert={p.meilensteinId}
              gesperrt={fest}
              optionen={meilensteineBestand.map((m) => ({ wert: m.id, name: m.name }))}
              setze={(w) => (w ? setze({ meilensteinId: w }) : ohne('meilensteinId'))}
            />
          )}
          {istAufgabe && (
            <>
              {(p.wunsch ?? []).map((w) => (
                <span
                  key={w}
                  className="flex items-center gap-1 rounded-full bg-[#eef3fb] px-2 py-0.5"
                >
                  ☆ {nameVon(w)}
                  {!fest && (
                    <button
                      type="button"
                      aria-label={`${nameVon(w)} als Wunsch entfernen`}
                      onClick={() => setze({ wunsch: (p.wunsch ?? []).filter((x) => x !== w) })}
                      className="cursor-pointer text-muted"
                    >
                      ×
                    </button>
                  )}
                </span>
              ))}
              {!fest && (p.wunsch ?? []).length < 5 && (
                <select
                  aria-label="Wunsch-Entwickler hinzufügen"
                  value=""
                  onChange={(e) =>
                    e.target.value && setze({ wunsch: [...(p.wunsch ?? []), e.target.value] })
                  }
                  className={FELD}
                >
                  <option value="">+ Wunsch</option>
                  {menschen
                    .filter((m) => !(p.wunsch ?? []).includes(m.id))
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                </select>
              )}
              <Wahl
                name="Bearbeiter"
                leer="– nur Wunsch –"
                wert={p.bearbeiter}
                gesperrt={fest}
                optionen={menschen.map((m) => ({ wert: m.id, name: m.name }))}
                setze={(w) => (w ? setze({ bearbeiter: w }) : ohne('bearbeiter'))}
              />
            </>
          )}
          {p.art !== 'vorhaben' && (
            <button
              type="button"
              onClick={() => setBeschreibungOffen((x) => !x)}
              className="cursor-pointer font-semibold text-accent-deep"
            >
              {p.beschreibung ? 'Beschreibung' : fest ? '' : '+ Beschreibung'}
            </button>
          )}
          {!fest && p.art !== 'vorhaben' && (
            <button
              type="button"
              aria-label={`${p.titel} aus dem Entwurf nehmen`}
              onClick={() => weg(p.schluessel)}
              className="cursor-pointer text-muted hover:text-bad"
            >
              entfernen
            </button>
          )}
          {telefon && (
            <button
              type="button"
              onClick={() => setAufgeklappt(false)}
              className="cursor-pointer font-semibold text-accent-deep"
            >
              fertig ▴
            </button>
          )}
        </div>
      )}

      {(beschreibungOffen || (p.art === 'vorhaben' && p.beschreibung)) && (
        <div className="mt-1 ml-[38px]">
          {fest ? (
            <div className="text-[12.5px]">
              <Markdown text={p.beschreibung ?? ''} kompakt />
            </div>
          ) : (
            <textarea
              aria-label="Beschreibung"
              value={p.beschreibung ?? ''}
              onChange={(e) => setze({ beschreibung: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-line2 bg-white px-2 py-1 text-[12.5px]"
            />
          )}
        </div>
      )}

      {befunde.length > 0 && (
        <ul className="mt-1 ml-[38px] flex flex-col gap-0.5">
          {befunde.map((b) => (
            <BefundZeile key={b.text} b={b} />
          ))}
        </ul>
      )}
    </div>
  )
}

function Wahl({
  name,
  wert,
  optionen,
  setze,
  gesperrt,
  leer,
}: {
  name: string
  wert: string | undefined
  optionen: { wert: string; name: string }[]
  setze: (w: string) => void
  gesperrt: boolean
  leer?: string
}) {
  return (
    <label className="flex items-center gap-1">
      <span className="sr-only">{name}</span>
      <select
        aria-label={name}
        value={wert ?? ''}
        disabled={gesperrt}
        onChange={(e) => setze(e.target.value)}
        className={cn(FELD, !wert && 'text-muted')}
      >
        <option value="">{leer ?? `${name} –`}</option>
        {optionen.map((o) => (
          <option key={o.wert} value={o.wert}>
            {name === 'Bearbeiter' ? `👤 ${o.name}` : o.name}
          </option>
        ))}
      </select>
    </label>
  )
}

/** Wo das Angelegte steht – Aufgaben öffnen ihr Detail hier, Vorhaben die Vorhaben-Ansicht. */
function ErgebnisVerweis({ p, vorhabenId }: { p: PlanPosten; vorhabenId?: string | null }) {
  const oeffnen = useAufgabeOeffnen()
  const stil = 'cursor-pointer text-[12px] font-semibold text-good-text hover:underline'
  if (p.art === 'aufgabe') {
    return (
      <button type="button" onClick={() => oeffnen(p.ergebnis!)} className={stil}>
        ✓ angelegt ↗
      </button>
    )
  }
  if (p.art === 'unteraufgabe') return <span className={stil}>✓ angelegt</span>
  const v = p.art === 'vorhaben' ? p.ergebnis : vorhabenId
  return (
    <a href={v ? `/vorhaben?vorhaben=${v}` : '/vorhaben'} className={stil}>
      ✓ angelegt ↗
    </a>
  )
}

function Aktionen({
  sicht,
  posten,
  fehlerZahl,
  schmutzig,
  setMeldung,
  neuLaden,
}: {
  sicht: Sicht
  posten: PlanPosten[]
  fehlerZahl: number
  schmutzig: boolean
  setMeldung: (m: { art: 'ok' | 'fehler'; text: string } | null) => void
  neuLaden: () => void
}) {
  const [sicher, setSicher] = useState(false)
  const verwerfen = useMutation({
    mutationFn: () => trpc.planEntwurf.verwerfen.mutate({ id: sicht.id }),
    onSuccess: () => {
      setMeldung({ art: 'ok', text: 'Entwurf verworfen – im Bestand ist nichts entstanden.' })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })
  const queryClient = useQueryClient()
  const uebernehmen = useMutation({
    mutationFn: () =>
      trpc.planEntwurf.uebernehmen.mutate({ id: sicht.id, basisFassung: sicht.fassung }),
    onSuccess: (r) => {
      setMeldung(
        r.abbruch
          ? {
              art: 'fehler',
              text: `${r.angelegt} angelegt, dann abgebrochen: ${r.abbruch}\nDas Angelegte bleibt – „Übernehmen" setzt beim nächsten Mal dort fort.`,
            }
          : {
              art: 'ok',
              text: `Übernommen – ${r.angelegt} Posten angelegt, gekennzeichnet als „von Claude geplant".`,
            },
      )
      void queryClient.invalidateQueries({ queryKey: ['vorhaben'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      neuLaden()
    },
    onError: (e: Error) => setMeldung({ art: 'fehler', text: e.message }),
  })
  const angehakt = posten.filter((p) => p.an && !p.ergebnis).length
  const gesperrt = fehlerZahl > 0 || schmutzig || uebernehmen.isPending || angehakt === 0
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <Button
        disabled={gesperrt}
        className={cn(gesperrt && 'cursor-not-allowed opacity-50')}
        onClick={() => uebernehmen.mutate()}
        title="Legt die angehakten Posten mit deinen Rechten an"
      >
        {uebernehmen.isPending
          ? 'legt an …'
          : sicht.stand === 'teilweise'
            ? `Fortsetzen (${angehakt})`
            : `Übernehmen (${angehakt})`}
      </Button>
      {fehlerZahl > 0 && (
        <span className="text-[12px] text-bad">{fehlerZahl} Fehler – erst beheben</span>
      )}
      {schmutzig && <span className="text-[12px] text-muted">speichert …</span>}
      <span className="flex-1" />
      {sicher ? (
        <>
          <span className="text-[12.5px] text-ink2">Wirklich verwerfen?</span>
          <Button variant="ghost" size="sm" onClick={() => setSicher(false)}>
            Nein
          </Button>
          <Button size="sm" variant="ghost" className="text-bad" onClick={() => verwerfen.mutate()}>
            Ja, verwerfen
          </Button>
        </>
      ) : (
        <Button variant="ghost" size="sm" onClick={() => setSicher(true)}>
          Verwerfen
        </Button>
      )}
    </div>
  )
}

function Rueckmeldung({
  sicht,
  gesperrt,
  neuLaden,
}: {
  sicht: Sicht
  gesperrt: boolean
  neuLaden: () => void
}) {
  const [text, setText] = useState(sicht.rueckmeldung ?? '')
  const [gesendet, setGesendet] = useState(false)
  const senden = useMutation({
    mutationFn: () => trpc.planEntwurf.rueckmelden.mutate({ id: sicht.id, text }),
    onSuccess: () => {
      setGesendet(true)
      neuLaden()
    },
  })
  if (gesperrt && !sicht.rueckmeldung) return null
  return (
    <div className={cn('mt-5 rounded-[12px] border px-3 py-2.5', RAHMEN)}>
      <div className={cn('text-[12.5px] font-semibold', VIOLETT)}>Rückmeldung an Claude</div>
      {gesperrt ? (
        <p className="mt-1 text-[13px] whitespace-pre-wrap">{sicht.rueckmeldung}</p>
      ) : (
        <>
          <textarea
            aria-label="Rückmeldung an Claude"
            value={text}
            onChange={(e) => {
              setText(e.target.value)
              setGesendet(false)
            }}
            rows={3}
            placeholder="z. B. „Schulung erst im Februar, dafür den Pilot zwei Wochen früher“"
            className="mt-1 w-full rounded-md border border-line2 bg-white px-2 py-1.5 text-[13px]"
          />
          <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-ink2">
            <Button size="sm" disabled={senden.isPending} onClick={() => senden.mutate()}>
              Speichern
            </Button>
            {(gesendet || sicht.rueckmeldungOffen) && (
              <span>
                Liegt für Claude bereit. Sag Claude:{' '}
                <em>„Überarbeite den Entwurf {sicht.titel}"</em>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/** **Alle Entwürfe von Claude** – offen oben, darunter, was entschieden ist. */
export function PlanEntwuerfe() {
  const { data: liste = [], isLoading } = useQuery({
    queryKey: ['planEntwurf', 'liste'],
    queryFn: () => trpc.planEntwurf.liste.query(),
  })
  const offen = liste.filter((e) => planEntwurfOffen(e.stand))
  const rest = liste.filter((e) => !planEntwurfOffen(e.stand))
  const zeile = (e: (typeof liste)[number]) => (
    <li key={e.id}>
      <a
        href={`/entwurf/${e.id}`}
        className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 border-b border-line px-1 py-2 last:border-b-0 hover:bg-[#faf8ff]"
      >
        <span className={cn('font-semibold', VIOLETT)}>✦</span>
        <span className="min-w-0 flex-1 text-[14px] font-semibold">{e.titel}</span>
        <span className="text-[12px] text-ink2">
          {e.weltName ?? e.welt}
          {e.zielVorhaben ? ` · ergänzt ${e.zielVorhaben}` : ''} · Fassung {e.fassung} ·{' '}
          {STAND[e.stand]}
        </span>
        <span className="tabular text-[12px] text-muted">
          {new Date(e.geaendertAm).toLocaleDateString('de-DE')}
        </span>
      </a>
    </li>
  )
  return (
    <>
      <Topbar
        titel="Entwürfe von Claude"
        untertitel="Pläne, die Claude vorgeschlagen hat – angelegt wird erst beim Übernehmen"
      />
      <Card>
        <CardPad>
          {isLoading ? (
            <p className="text-[13px] text-muted">lädt …</p>
          ) : liste.length === 0 ? (
            <p className="text-[13px] text-ink2">
              Noch keine. Bitte Claude (claude.ai oder Claude Code mit dem Connector „NetCon Work"):{' '}
              <em>„Plane mir das Vorhaben … bis …"</em> – der Entwurf erscheint dann hier.
            </p>
          ) : (
            <>
              {offen.length > 0 && <ul>{offen.map(zeile)}</ul>}
              {rest.length > 0 && (
                <details className="mt-3" open={offen.length === 0}>
                  <summary className="cursor-pointer text-[12.5px] font-semibold text-ink2">
                    Entschieden · {rest.length}
                  </summary>
                  <ul>{rest.map(zeile)}</ul>
                </details>
              )}
            </>
          )}
        </CardPad>
      </Card>
    </>
  )
}
